import { JsonObject } from "@/domains/common/types";
import { _compactStringForLogging } from "@/modules/string/compact-for-logging";

type CompactUrlForLoggingOptions = {
  allowedQueryParams?: string[];
  includeQueryKeys?: boolean;
  maxPathLength?: number;
  maxQueryValueLength?: number;
};

const DEFAULT_MAX_PATH_LENGTH = 160;
const DEFAULT_MAX_QUERY_VALUE_LENGTH = 80;
const SENSITIVE_QUERY_PARAM_PATTERN =
  /(authorization|cookie|token|secret|password|passwd|pwd|signature|signed|api[_-]?key|access[_-]?key|auth|code)/i;

const normalizeAllowedQueryParams = (allowedQueryParams: string[] | undefined) => {
  return new Set((allowedQueryParams ?? []).map(param => param.toLowerCase()));
};

export const compactWindowUrlForLogging = (
  value: URL | string,
  {
    allowedQueryParams,
    includeQueryKeys = true,
    maxPathLength = DEFAULT_MAX_PATH_LENGTH,
    maxQueryValueLength = DEFAULT_MAX_QUERY_VALUE_LENGTH,
  }: CompactUrlForLoggingOptions = {}
): JsonObject => {
  const rawUrl = value.toString();

  try {
    const url = value instanceof URL ? value : new URL(value);
    const allowedParams = normalizeAllowedQueryParams(allowedQueryParams);
    const queryKeys = Array.from(new Set(Array.from(url.searchParams.keys()))).sort();
    const query: JsonObject = {};

    for (const key of queryKeys) {
      const shouldIncludeValue =
        allowedParams.has(key.toLowerCase()) && !SENSITIVE_QUERY_PARAM_PATTERN.test(key);

      query[key] = shouldIncludeValue
        ? (_compactStringForLogging(url.searchParams.get(key) ?? "", {
            maxLength: maxQueryValueLength,
          }) as JsonObject)
        : "[redacted]";
    }

    return {
      hash: _compactStringForLogging(rawUrl, { maxLength: 80 }).hash,
      host: url.host,
      origin: url.origin,
      path: _compactStringForLogging(url.pathname, { maxLength: maxPathLength }) as JsonObject,
      protocol: url.protocol,
      ...(allowedParams.size ? { query } : {}),
      queryKeyCount: queryKeys.length,
      ...(includeQueryKeys ? { queryKeys } : {}),
    };
  } catch {
    const rawSummary = _compactStringForLogging(rawUrl, { maxLength: maxPathLength });

    return {
      hash: rawSummary.hash,
      invalid: true,
      length: rawSummary.length,
    };
  }
};
